import { useEffect, useRef } from 'react';
import { addMinutes, subHours } from 'date-fns';
import { listAppointments, upsertAppointment } from '../api/appointments';
import { recordAppointmentUpdate } from '../api/updates/updates';

const TEN_MINUTES_MS = 10 * 60 * 1000;
const CHECK_INTERVAL_MS = 2 * 60 * 1000;
const PAST_WINDOW_HOURS = 24;

function getEndTime(appointment) {
  const dateValue = appointment?.dateTime;
  if (!dateValue) return null;
  const startDate = dateValue?.toDate ? dateValue.toDate() : new Date(dateValue);
  if (isNaN(startDate.getTime())) return null;
  const duration = parseInt(
    appointment?.duration ??
    appointment?.serviceDuration ??
    appointment?.service?.duration ??
    0,
    10
  );
  return addMinutes(startDate, duration);
}

function shouldAutoComplete(appointment) {
  if (!appointment?.id) return false;
  if (appointment.source === 'demo-screenshot' || appointment.demoBatchId) return false;
  const status = (appointment.status || '').toLowerCase();
  if (status === 'completed' || status === 'cancelled' || status === 'no show') return false;
  const endTime = getEndTime(appointment);
  if (!endTime) return false;
  const deadline = endTime.getTime() + TEN_MINUTES_MS;
  return Date.now() >= deadline;
}

export function useBackgroundAppointmentCompletion(companyId) {
  const completedIdsRef = useRef(new Set());
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!companyId) return;

    const runCheck = async () => {
      try {
        const now = new Date();
        const since = subHours(now, PAST_WINDOW_HOURS);
        const result = await listAppointments({
          companyId,
          from: since,
          to: now,
          archived: false,
        });

        for (const appointment of result.appointments || []) {
          const key = appointment.id;
          if (completedIdsRef.current.has(key)) continue;
          if (!shouldAutoComplete(appointment)) continue;

          const nowIso = new Date().toISOString();
          await upsertAppointment({
            companyId,
            id: appointment.id,
            appointment: {
              status: 'completed',
              completedAt: nowIso,
              updatedAt: nowIso,
              updatedBy: 'employee',
              lastAction: 'completed',
            },
          });
          void recordAppointmentUpdate({
            companyId,
            appointment,
            type: 'completed',
            additionalDetails: {
              updatedBy: 'employee',
              lastAction: 'completed',
            },
          }).catch((error) => {
            console.warn('Non-blocking: failed to write auto-complete update', error);
          });
          completedIdsRef.current.add(key);
        }
      } catch (err) {
        if (err?.status !== 403) {
          console.error('Background appointment completion check failed:', err);
        }
      }
    };

    runCheck();
    intervalRef.current = setInterval(runCheck, CHECK_INTERVAL_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [companyId]);
}
